import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Loading, useStateUrl } from '@virtidev/toolbox';
import PageLoadError from '@base/components/PageLoadError';
import { useQuery } from '@apollo/client';
import { REACT_VIRTUAL_HUMAN_SHARE_TOKEN } from '@core/components/VirtualHumans/PDWebPlayerContent/PDWebPlayerPageContent.query';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import RegisterBanner from '@core/components/Signup/RegisterBanner/RegisterBanner';
import useUser from '@core/helpers/useUser';
import { vhPlayerSupportsDevice } from '@base/utility/BrowserCheck';
import {
  GlobalStyleForVHPublicPlayer,
  MobileMessage,
  VirtualHuman,
} from '@core/pages/admin/virtual-humans/VHWebPlayerPagePublic/VHWebPlayerPagePublic.styled';
import { generateDeepLink } from '@core/components/DeepLink/helpers/generateDeepLink';
import { CHECK_LOGIN } from '@base/queries/UserQueries';
import usePublicToken from '@core/helpers/usePublicToken';
import { useIsIframe } from '@core/helpers/useIsIframe';
import FeedbackFormEmbed from '@core/components/FeedbackForms/FeedbackFormEmbed';
import _ from 'lodash';
import { useShouldShowFeedbackForms } from '@core/helpers/useShouldShowFeedbackForms';

const VHWebPlayerPagePublic = () => {
  const { sharetoken } = useParams();
  const { ID, Organisations } = useUser();
  const [loaded, setLoaded] = useState(false);
  const { pathname } = useLocation();
  const { value: redirect } = useStateUrl({ key: 'redirect' });
  const embedded = useIsIframe();
  // use state instead of useStateUrl because refreshing causes issues
  // with state management in terms of knowing when "Continue to form"
  // needs to be rendered
  // this could be mitigated by providing a query param if stage is complete
  // but that's a bit hacky? I'm not entirely sure why the manager normally
  // uses URL for state tbh
  const [stage, setStage] = useState('before');

  const loggedin = ID && ID !== '0';

  const {
    data: publicData,
    error: publicError,
    loading: publicLoading,
    called,
  } = usePublicToken({ skip: loggedin });

  const {
    data: loggedinData,
    loading: loggedinLoading,
    error: loggedinError,
  } = useQuery(CHECK_LOGIN, {
    variables: {
      ID,
    },
    skip: !loggedin,
  });

  const { data, loading, error } = useQuery(REACT_VIRTUAL_HUMAN_SHARE_TOKEN, {
    variables: { sharetokenHeader: sharetoken, sharetoken },
    skip: !sharetoken || (called && !publicData),
  });

  const virtualhuman = useMemo(() => data?.readOneVirtualHuman, [data]);

  const inOrg = useMemo(
    () =>
      Organisations?.some?.(
        ({ ID: orgId }) => virtualhuman?.OrganisationID === orgId
      ),
    [Organisations, virtualhuman?.OrganisationID]
  );

  const deepLink = useMemo(() => {
    if (!virtualhuman?.ID || redirect === '1' || embedded) {
      return null;
    }
    const search = new URLSearchParams({
      vhID: virtualhuman?.ID,
      content: 'true',
      public: 'true',
      organisationID: inOrg ? virtualhuman?.OrganisationID : 'null',
    });

    return generateDeepLink(`${pathname}?${search.toString()}`, null);
  }, [virtualhuman, redirect, embedded, inOrg, pathname]);

  const goToMobileDeepLink = useCallback(() => {
    if (deepLink) {
      window.onbeforeunload = null;

      window.location.href = deepLink;
    }
  }, [deepLink]);

  useEffect(() => {
    if (!virtualhuman) {
      return;
    }
    localStorage.setItem(
      'publicVirtualHumanAPI',
      virtualhuman.Organisation.VirtualHumanAPI
    );
    setLoaded(true);
  }, [virtualhuman]);

  const shouldRedirectWebapp = !embedded && loggedin && inOrg;
  const gotoNextStage = useCallback(() => {
    switch (stage) {
      case 'before':
        setStage('vh');
        return;
      case 'vh':
        setStage('after');
        return;
      case 'after':
        setStage('complete');
        return;
      default:
        return;
    }
  }, [stage, setStage]);

  const isIframe = useIsIframe();
  const showRegisterPrompt =
    virtualhuman?.ShowRegister && !inOrg && !embedded && isIframe;

  const [beforeVHForm, afterVHForm] = useShouldShowFeedbackForms(
    data?.readOneVirtualHuman?.FeedbackFormBefore,
    data?.readOneVirtualHuman?.FeedbackFormAfter,
    data?.readOneVirtualHuman?.FeedbackFormBeforeCompleted,
    data?.readOneVirtualHuman?.FeedbackFormAfterCompleted,
    false,
    !loggedin
  );

  // initial stage value after data is loaded
  useEffect(() => {
    if (!data || ['before', 'vh', 'after', 'complete'].includes(stage)) {
      return;
    }

    setStage('before');
  }, [stage, data, setStage]);

  let feedbackFormContent = null;

  if (stage === 'before') {
    feedbackFormContent = (
      <FeedbackFormEmbed
        hash={beforeVHForm?.Hash}
        formID={beforeVHForm?.ID}
        onComplete={gotoNextStage}
        trigger="BEGIN_VH"
        sourceID={data?.readOneVirtualHuman?.ID}
        userID={
          loggedinData?.readOneMember?.ID ??
          publicData?.createPublicToken?.Member?.ID
        }
      />
    );
  }

  if (stage === 'after') {
    feedbackFormContent = (
      <FeedbackFormEmbed
        hash={afterVHForm?.Hash}
        formID={afterVHForm?.ID}
        onComplete={gotoNextStage}
        trigger="END_VH"
        sourceID={data?.readOneVirtualHuman?.ID}
        userID={
          loggedinData?.readOneMember?.ID ??
          publicData?.createPublicToken?.Member?.ID
        }
      />
    );
  }

  if (!vhPlayerSupportsDevice(virtualhuman?.PlayerType)) {
    return (
      <>
        {showRegisterPrompt && (
          <RegisterBanner
            type="virtualhuman"
            sharetoken={sharetoken}
            loggedin={loggedin}
          />
        )}
        <MobileMessage>
          <div>
            Unfortunately, web preview is currently only available in latest
            version of Google Chrome, Mozilla Firefox, and Microsoft Edge on a
            computer.
          </div>
          {deepLink && (
            <div>
              <Button color="primary" onClick={goToMobileDeepLink}>
                Use the Virti App
              </Button>
            </div>
          )}
        </MobileMessage>
      </>
    );
  }

  if (loading || !loaded || loggedinLoading || publicLoading) {
    return <Loading />;
  }
  if (error || publicError) {
    return <PageLoadError graphQLErrorObj={error} />;
  }

  if (!sharetoken || !virtualhuman) {
    return null;
  }

  if (shouldRedirectWebapp || loggedinError) {
    const validLogin = loggedinData?.readOneMember?.ID === ID;

    if (validLogin) {
      return (
        <Redirect
          to={`/my-virtual-humans/${virtualhuman.ID}/play/${sharetoken}`}
        />
      );
    }
    const message = 'Your login had expired, please login again';
    return (
      <Redirect
        to={{
          pathname: '/logout',
          search: `msg=${encodeURIComponent(message)}`,
          state: {
            // bring them back to the content page after logging in
            prevPath: `/my-virtual-humans/${virtualhuman.ID}/play/${sharetoken}`,
          },
        }}
      />
    );
  }

  if (virtualhuman.ShareLoginRequired) {
    if (!loggedin) {
      const message =
        'An account is required for this content, please logn to continue';
      return (
        <Redirect
          to={{
            pathname: '/logout',
            search: `msg=${encodeURIComponent(message)}`,
            state: {
              // bring them back to the content page after logging in
              prevPath: `/embed/my-virtual-humans/${sharetoken}`,
            },
          }}
        />
      );
    }
    if (!inOrg) {
      return (
        <>
          {showRegisterPrompt && (
            <RegisterBanner
              type="virtualhuman"
              sharetoken={sharetoken}
              loggedin={loggedin}
            />
          )}
          <MobileMessage>
            <div>
              Unfortunately, it seems that you do not have access to this
              content. Please contact your administrator to resolve this.
            </div>
          </MobileMessage>
        </>
      );
    }
  }

  return (
    <>
      <GlobalStyleForVHPublicPlayer />
      {feedbackFormContent}
      {showRegisterPrompt && (
        <RegisterBanner
          type="virtualhuman"
          sharetoken={sharetoken}
          loggedin={loggedin}
        />
      )}
      <VirtualHuman
        onComplete={gotoNextStage}
        virtualHumanId={virtualhuman?.ID}
        sharetoken={sharetoken}
        publicPlayer
        show={stage === 'vh' || stage === 'complete'}
      />
    </>
  );
};

export default VHWebPlayerPagePublic;
